import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { Table, type Column } from 'grabbitbot-legacy-ui'
import { statisticsClient } from '../../api'
import type { Transaction } from '../../api/statisticsTypes'
import { formatNumberByCoin } from '../trading/coinConfigLabels'
import { formatDateTime } from '../trading/tradingConstants'
import { coinSheetLink, pairLabel } from './positionLinks'
import { StatisticsParametersPicker } from './StatisticsParametersPicker'
import { useStatisticsParams } from './statisticsParams'
import { failureText } from '../../api/errorMessages'

const PAGE_SIZE = 50

function num(value: unknown): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

/**
 * Equivalent of legacy `V3StatisticsTransactionListComponent` (route
 * `.../statistics/transactions`, contract #10). Filters come ONLY from URL
 * query params (`tag`/`coin1`/`coin2`) — the header's nav links are what set
 * them (e.g. `?tag=source_autobuy`); this page has no filter inputs of its
 * own. The endpoint has no pagination envelope, so paging is client-side
 * over the full answer, same as legacy.
 */
export function TransactionsPage() {
  const { exchangeId = '', tradingType = 'spot' } = useParams<{ exchangeId: string; tradingType: string }>()
  const [searchParams] = useSearchParams()
  if (tradingType !== 'spot') {
    return (
      <div className="portal-card">
        <h1>Transactions</h1>
        <p className="portal-notice portal-notice--warning">Margin trading is not supported.</p>
      </div>
    )
  }

  const tag = searchParams.get('tag') ?? ''
  const coin1 = searchParams.get('coin1') ?? ''
  const coin2 = searchParams.get('coin2') ?? ''

  return (
    <SpotTransactions
      key={`${exchangeId}:${tag}:${coin1}:${coin2}`}
      exchangeId={exchangeId}
      tag={tag}
      coin1={coin1}
      coin2={coin2}
    />
  )
}

function SpotTransactions({
  exchangeId,
  tag,
  coin1,
  coin2,
}: {
  exchangeId: string
  tag: string
  coin1: string
  coin2: string
}) {
  const { days, preset, setPreset } = useStatisticsParams()
  const [items, setItems] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>(null)
  const [page, setPage] = useState(1)
  const [expanded, setExpanded] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setPage(1)
    statisticsClient
      .transactions(exchangeId, { days, tag: tag || undefined, coin1: coin1 || undefined, coin2: coin2 || undefined })
      .then((result) => {
        if (cancelled) return
        setItems(result.items)
        setError(null)
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err ?? true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [exchangeId, days, tag, coin1, coin2])

  const pageCount = Math.max(1, Math.ceil(items.length / PAGE_SIZE))
  const pageItems = useMemo(() => items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [items, page])
  const offset = (page - 1) * PAGE_SIZE

  const columns: Column<Transaction>[] = [
    {
      key: 'coinPair',
      label: 'Coinpair',
      align: 'left',
      sortValue: (r) => pairLabel(r.coin1, r.coin2),
      format: (_v, r) => <Link to={coinSheetLink(exchangeId, r.coin1, r.coin2)}>{pairLabel(r.coin1, r.coin2)}</Link>,
    },
    { key: 'date', label: 'Date', align: 'left', sortValue: (r) => r.date, format: (v) => formatDateTime(v as string) },
    { key: 'side', label: 'Type', align: 'left' },
    { key: 'executedPrice', label: 'Price', numeric: true, sortValue: (r) => num(r.executedPrice), format: (v) => num(v).toFixed(8) },
    {
      key: 'executedQuantity',
      label: 'Quantity',
      numeric: true,
      sortValue: (r) => num(r.executedQuantity),
      format: (_v, r) => `${formatNumberByCoin(num(r.executedQuantity), r.coin1)} ${r.coin1}`,
    },
    {
      key: 'executedAmount',
      label: 'Amount',
      numeric: true,
      sortValue: (r) => num(r.executedAmount),
      format: (_v, r) => `${formatNumberByCoin(num(r.executedAmount), r.coin2)} ${r.coin2}`,
    },
    {
      key: 'details',
      label: '',
      sortable: false,
      format: (_v, r) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            const i = pageItems.indexOf(r)
            setExpanded((prev) => (prev === i ? null : i))
          }}
        >
          {expanded === pageItems.indexOf(r) ? 'hide details' : 'show details'}
        </button>
      ),
    },
  ]

  const activeFilters = [
    coin1 && `Base asset : ${coin1}`,
    coin2 && `Quote asset : ${coin2}`,
    tag && `Tag : ${tag}`,
  ].filter(Boolean) as string[]

  return (
    <div>
      <div className="portal-page-title">
        <h1>Coin transactions</h1>
      </div>
      <div className="portal-card">
        <div className="portal-toolbar">
          <StatisticsParametersPicker preset={preset} onChange={setPreset} />
        </div>

        {activeFilters.length > 0 && (
          <ul>
            {activeFilters.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        )}

        {loading && <p>Loading…</p>}
        {error !== null && <p role="alert">{failureText(error, 'Could not load transactions')}</p>}

        {!loading && !error && (
          <>
            <h3>
              {items.length} transactions found in the last {days} days.
            </h3>
            <div className="portal-table-scroll">
              <Table
                columns={columns}
                rows={pageItems}
                rowKey={(r, i) => `${r.orderId ?? ''}-${r.date}-${i}`}
                renderExpanded={(r) => {
                  const i = pageItems.indexOf(r)
                  if (expanded !== i) return null
                  return (
                    <tr className="portal-detail-row">
                      <td colSpan={columns.length}>
                        {r.orderId && <div>Order ID: {r.orderId}</div>}
                        {r.clientOrderId && <div>Client Order ID: {r.clientOrderId}</div>}
                        {r.tags.length > 0 && <div>Tags: {r.tags.join(', ')}</div>}
                      </td>
                    </tr>
                  )
                }}
              />
            </div>
            <nav className="gbx-pager" aria-label="Pagination">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </button>
              <span>
                Page {page} of {pageCount} — rows {offset + 1}-{Math.min(offset + PAGE_SIZE, items.length)} of {items.length}
              </span>
              <button type="button" disabled={page >= pageCount} onClick={() => setPage((p) => p + 1)}>
                Next
              </button>
            </nav>
          </>
        )}
      </div>
    </div>
  )
}
